/**
 * OrdersCatalogSettings — tab Impostazioni per la gestione del catalogo
 * prodotti ordinabili (OPS-only). CRUD + mappatura categoria Magazzino.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Loader2, Package, Pencil, Plus, Power, PowerOff, Search, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { fmtPrice } from '../pages/OrdersPage';

const EMPTY = { code: '', name: '', category: '', price: '', price_note: '', inventory_category_id: '' };

export default function OrdersCatalogSettings() {
  const [products, setProducts] = useState(null);
  const [invCats, setInvCats] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null | {product or EMPTY}
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [autoMapping, setAutoMapping] = useState(false);

  const autoMap = async () => {
    if (!window.confirm('Mappare automaticamente i prodotti non mappati alle categorie Magazzino?\n(Clipper/Trimmer → Tagliacapelli, Shaver/Rasoi → Rasoio, Forbici → Forbici, Phon → Asciugacapelli. Pettini, spazzole, ricambi e accessori restano esclusi.)')) return;
    setAutoMapping(true);
    try {
      const r = await api.post('/orders/catalog/auto-map');
      const skipped = r.data.skipped_categories || [];
      toast.success(`${r.data.mapped} prodotti mappati al Magazzino${skipped.length ? ` — categorie escluse: ${skipped.join(', ')}` : ''}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-mappatura fallita');
    } finally {
      setAutoMapping(false);
    }
  };

  const load = () => {
    api.get('/orders/catalog', { params: { include_inactive: true } })
      .then(r => setProducts(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Errore caricamento catalogo'));
  };

  useEffect(() => {
    load();
    api.get('/inventory/categories').then(r => setInvCats(r.data || [])).catch(() => {});
  }, []);

  const categories = useMemo(
    () => [...new Set((products || []).map(p => p.category))],
    [products]
  );

  const grouped = useMemo(() => {
    const s = search.trim().toLowerCase();
    const filtered = (products || []).filter(p =>
      !s || p.name.toLowerCase().includes(s) || (p.code || '').toLowerCase().includes(s));
    const map = new Map();
    filtered.forEach(p => {
      if (!map.has(p.category)) map.set(p.category, []);
      map.get(p.category).push(p);
    });
    return [...map.entries()];
  }, [products, search]);

  const openEdit = (p) => {
    setForm(p ? {
      code: p.code || '', name: p.name, category: p.category,
      price: p.price != null ? String(p.price) : '',
      price_note: p.price_note || '',
      inventory_category_id: p.inventory_category_id || '',
    } : EMPTY);
    setEditing(p || 'new');
  };

  const save = async () => {
    if (!form.name.trim() || !form.category.trim()) { toast.error('Nome e categoria sono obbligatori'); return; }
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      category: form.category.trim(),
      price: form.price !== '' ? parseFloat(String(form.price).replace(',', '.')) : null,
      price_note: form.price_note.trim() || null,
      inventory_category_id: form.inventory_category_id || null,
    };
    if (payload.price !== null && Number.isNaN(payload.price)) { toast.error('Prezzo non valido'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/orders/catalog', payload);
        toast.success('Prodotto aggiunto');
      } else {
        await api.put(`/orders/catalog/${editing.product_id}`, payload);
        toast.success('Prodotto aggiornato');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio fallito');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      if (p.active === false) {
        await api.put(`/orders/catalog/${p.product_id}`, { active: true });
        toast.success('Prodotto riattivato');
      } else {
        await api.delete(`/orders/catalog/${p.product_id}`);
        toast.success('Prodotto disattivato');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operazione fallita');
    }
  };

  return (
    <div className="space-y-4" data-testid="orders-catalog-settings">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Package className="w-5 h-5" /> Catalogo Ordini
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Prodotti ordinabili dai manager. Mappa la &quot;categoria Magazzino&quot; per caricare
            automaticamente gli strumenti alla consegna dell&apos;ordine.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={autoMap} disabled={autoMapping} className="gap-1.5" data-testid="catalog-automap-btn">
            {autoMapping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Auto-mappa Magazzino
          </Button>
          <Button onClick={() => openEdit(null)} className="gap-1.5" data-testid="catalog-add-btn">
            <Plus className="w-4 h-4" /> Nuovo prodotto
          </Button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca per nome o codice…"
               className="rounded-none pl-8" data-testid="catalog-search-input" />
      </div>

      {products === null ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : (
        <div className="border border-slate-200 divide-y divide-slate-100 bg-white">
          {grouped.map(([cat, prods]) => (
            <div key={cat}>
              <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50">
                {cat} <span className="font-normal">({prods.length})</span>
              </p>
              {prods.map(p => {
                const invCat = invCats.find(c => c.category_id === p.inventory_category_id);
                return (
                  <div key={p.product_id} className={`flex items-center gap-2 px-3 py-2 ${p.active === false ? 'opacity-50' : ''}`}
                       data-testid={`catalog-row-${p.product_id}`}>
                    <span className="font-mono text-[11px] text-slate-400 w-24 shrink-0 truncate">{p.code || '—'}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-800 truncate">{p.name}</p>
                      {invCat && <p className="text-[10px] text-blue-600">→ Magazzino: {invCat.name}</p>}
                    </div>
                    {p.price_note && <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-300 px-1.5 py-0.5 shrink-0">{p.price_note}</span>}
                    <span className="text-sm font-medium text-slate-700 w-20 text-right shrink-0">{p.price != null ? fmtPrice(p.price) : '—'}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)} data-testid={`catalog-edit-${p.product_id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggleActive(p)}
                            title={p.active === false ? 'Riattiva' : 'Disattiva'} data-testid={`catalog-toggle-${p.product_id}`}>
                      {p.active === false ? <Power className="w-3.5 h-3.5 text-emerald-600" /> : <PowerOff className="w-3.5 h-3.5 text-slate-400" />}
                    </Button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={v => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-lg" data-testid="catalog-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Nuovo prodotto' : 'Modifica prodotto'}</DialogTitle>
            <DialogDescription>Prodotto del catalogo ordinabile dai manager.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 block mb-1">Codice</label>
                <Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                       className="rounded-none" data-testid="catalog-form-code" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 block mb-1">Categoria *</label>
                <Input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                       list="catalog-categories" placeholder="Es. CLIPPER" className="rounded-none" data-testid="catalog-form-category" />
                <datalist id="catalog-categories">
                  {categories.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 block mb-1">Nome *</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                     className="rounded-none" data-testid="catalog-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 block mb-1">Prezzo listino €</label>
                <Input value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                       inputMode="decimal" placeholder="Es. 89.90" className="rounded-none" data-testid="catalog-form-price" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 block mb-1">Nota prezzo</label>
                <Input value={form.price_note} onChange={e => setForm(f => ({ ...f, price_note: e.target.value }))}
                       placeholder="Es. DA VERIFICARE" className="rounded-none" data-testid="catalog-form-price-note" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 block mb-1">Categoria Magazzino (carico automatico alla consegna)</label>
              <Select value={form.inventory_category_id || 'none'}
                      onValueChange={v => setForm(f => ({ ...f, inventory_category_id: v === 'none' ? '' : v }))}>
                <SelectTrigger className="rounded-none" data-testid="catalog-form-invcat">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nessuna (non va a Magazzino) —</SelectItem>
                  {invCats.map(c => (
                    <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="catalog-form-save">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
